import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  Progress,
  message as antdMessage,
} from 'antd';
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  CreditCardOutlined,
  DollarOutlined,
  EditOutlined,
  ExclamationCircleTwoTone,
  EyeOutlined,
  ReloadOutlined,
  SendOutlined,
  SettingOutlined,
  TeamOutlined,
  CrownOutlined,
  UsergroupAddOutlined,
  WarningOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import {
  changeCustomerInstance,
  fetchCustomerFicha,
  fetchInstances,
  resendFichaMessage,
} from '../services/customerFichaApi';
import {
  fetchGroupMembers,
  validateGroupMembers,
  setCustomerInstance,
} from '../services/customerGroupMembersApi';
import api from '../services/api';
import CallRecipientsPopover from '../components/organizer/CallRecipientsPopover';
import { absoluteUploadUrl } from '../utils/uploadUrl';

const readUser = () => {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
};

const { Title, Text, Paragraph } = Typography;

const CustomerFicha = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [resendingJobId, setResendingJobId] = useState(null);
  const [viewingMsg, setViewingMsg] = useState(null);
  const [editInstanceOpen, setEditInstanceOpen] = useState(false);
  const [instances, setInstances] = useState([]);
  const [instancesLoading, setInstancesLoading] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [savingInstance, setSavingInstance] = useState(false);
  const [sendingCharge, setSendingCharge] = useState(false);

  const currentUser = readUser();
  const isAdmin = currentUser?.profile === 'ADMINISTRADOR';
  // Admin + Suporte podem editar config de chamadas (grava audit_log).
  const canEditCallConfig = currentUser?.profile === 'ADMINISTRADOR' || currentUser?.profile === 'SUPORTE';

  const sendManualCharge = async (opts = {}) => {
    const { force = false } = opts;
    setSendingCharge(true);
    try {
      const res = await api.post(`/customers/${id}/manual-charge`, force ? { force: true } : {});
      antdMessage.success(`PIX enviado pro time ${res.data.team}`);
      load();
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data || {};
      const msg = data.message || 'Erro ao enviar PIX';
      // 422 = bloqueio "time sem jogos há 30d". Deixa admin sobrepor pra
      // casos de time voltando à ativa após pausa.
      if (status === 422 && !force) {
        Modal.confirm({
          title: 'Time sem jogos nos últimos 30 dias',
          content: (
            <div>
              <p>{msg}</p>
              <p style={{ marginTop: 12, color: '#666' }}>
                Esse bloqueio existe pra não gastar chip com time morto. Mas se for time voltando à ativa,
                pode enviar mesmo assim.
              </p>
            </div>
          ),
          okText: 'Enviar mesmo assim',
          okButtonProps: { danger: true },
          cancelText: 'Cancelar',
          onOk: () => sendManualCharge({ force: true }),
        });
      } else if (status === 429) {
        antdMessage.warning(msg);
      } else {
        antdMessage.error(msg);
      }
    } finally {
      setSendingCharge(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchCustomerFicha(id);
      setData(result);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        (err.response?.status === 403 ? 'Você não tem permissão para ver esta ficha' : null) ||
        (err.response?.status === 404 ? 'Time não encontrado' : null) ||
        'Não foi possível carregar a ficha';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleResendClick = useCallback(
    (item) => {
      Modal.confirm({
        title: 'Reenviar mensagem?',
        content: (
          <div>
            <Typography.Paragraph>
              A mesma mensagem será reenfileirada usando o chip <b>atual</b> do cadastro
              ({data?.whatsapp?.instance_name || '—'}) e o grupo {data?.whatsapp?.groupjid}.
            </Typography.Paragraph>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Job original: #{item.jobId} • {item.textLen} caracteres
            </Typography.Paragraph>
          </div>
        ),
        okText: 'Reenviar',
        cancelText: 'Cancelar',
        onOk: async () => {
          setResendingJobId(item.jobId);
          try {
            // Puxa o texto completo do item (o preview pode ter '…' se for >120 chars)
            const text = item.textPreview.endsWith('…')
              ? window.prompt(
                  'O texto da msg foi truncado no histórico. Cole o texto completo pra reenviar:',
                  item.textPreview.replace(/…$/, ''),
                )
              : item.textPreview;
            if (!text) return;
            const result = await resendFichaMessage(id, { text, sourceJobId: item.jobId });
            antdMessage.success(`Reenfileirada. Novo jobId: ${result.jobId}`);
            setTimeout(() => load(), 2000);
          } catch (err) {
            antdMessage.error(err.response?.data?.message || 'Falha ao reenviar');
          } finally {
            setResendingJobId(null);
          }
        },
      });
    },
    [id, data, load],
  );

  const openEditInstance = useCallback(async () => {
    setEditInstanceOpen(true);
    setSelectedInstance(data?.whatsapp?.instance_name || null);
    if (instances.length === 0) {
      setInstancesLoading(true);
      try {
        const res = await fetchInstances();
        const list = Array.isArray(res) ? res : res?.data || [];
        setInstances(list);
      } catch (err) {
        antdMessage.error('Não foi possível carregar as instâncias');
      } finally {
        setInstancesLoading(false);
      }
    }
  }, [instances.length, data]);

  const confirmChangeInstance = useCallback(async () => {
    if (!selectedInstance || selectedInstance === data?.whatsapp?.instance_name) {
      setEditInstanceOpen(false);
      return;
    }
    setSavingInstance(true);
    try {
      await changeCustomerInstance(id, selectedInstance);
      antdMessage.success(`Chip atualizado para "${selectedInstance}"`);
      setEditInstanceOpen(false);
      await load();
    } catch (err) {
      antdMessage.error(err.response?.data?.message || 'Falha ao trocar chip');
    } finally {
      setSavingInstance(false);
    }
  }, [id, selectedInstance, data, load]);

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" tip="Carregando ficha do time..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/customers')}>
            Voltar
          </Button>
          <Alert type="error" message="Erro" description={error} showIcon />
          <Button icon={<ReloadOutlined />} onClick={load}>
            Tentar novamente
          </Button>
        </Space>
      </div>
    );
  }

  if (!data) return null;

  const { customer, convocation, organizers, subscription, installments, games, supportLogs, players, whatsapp, welcome_audio_flow: welcomeAudio } = data;

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/customers')}>
          Voltar
        </Button>
        <Button icon={<ReloadOutlined />} onClick={load}>
          Atualizar
        </Button>
        {isAdmin && (
          <Popconfirm
            title="Enviar cobrança PIX manual?"
            description={`Vai gerar PIX de R$29,90 e enviar msg pra ${customer?.game_name || 'este time'}. Máx 1 envio a cada 6h.`}
            okText="Enviar"
            cancelText="Cancelar"
            onConfirm={sendManualCharge}
          >
            <Button
              type="primary"
              icon={<DollarOutlined />}
              loading={sendingCharge}
              danger
            >
              Enviar cobrança PIX
            </Button>
          </Popconfirm>
        )}
      </Space>

      <HeaderCard customer={customer} />

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <IdentityCard customer={customer} organizers={organizers}
            onCustomerPatched={(patch) => setData((d) => d ? { ...d, customer: { ...d.customer, ...patch } } : d)} />
        </Col>
        <Col xs={24} lg={12}>
          <SubscriptionCard subscription={subscription} installments={installments} />
        </Col>
        <Col xs={24}>
          <WhatsAppCard
            whatsapp={whatsapp}
            onEditInstance={openEditInstance}
            onResend={handleResendClick}
            resendingJobId={resendingJobId}
            onViewMsg={setViewingMsg}
          />
        </Col>
        <Col xs={24}>
          <WelcomeAudioFlowCard flow={welcomeAudio} customer={customer} />
        </Col>
        <Col xs={24} lg={12}>
          <ConvocationCard convocation={convocation} />
        </Col>
        <Col xs={24} lg={12}>
          <CallSettingsCard customer={customer} canEdit={canEditCallConfig}
            onSaved={(patch) => setData((d) => d ? { ...d, customer: { ...d.customer, ...patch } } : d)} />
        </Col>
        <Col xs={24} lg={12}>
          <GamesCard games={games} customerId={customer.id} />
          <HealthCard customerId={customer.id} />
        </Col>
        <Col xs={24}>
          <GroupMembersCard customerId={customer.id} onInstanceChanged={load} />
        </Col>
        <Col xs={24}>
          <PlayersCard players={players} />
        </Col>
        <Col xs={24}>
          <SupportLogsCard supportLogs={supportLogs} />
        </Col>
      </Row>

      <Modal
        title={
          <Space>
            <WarningOutlined style={{ color: '#ff4d4f' }} />
            <span>Trocar chip do time</span>
          </Space>
        }
        open={editInstanceOpen}
        onCancel={() => setEditInstanceOpen(false)}
        onOk={confirmChangeInstance}
        okText="Trocar chip"
        okButtonProps={{
          danger: true,
          disabled: !selectedInstance || selectedInstance === whatsapp?.instance_name,
          loading: savingInstance,
        }}
        cancelText="Cancelar"
      >
        <Alert
          type="error"
          showIcon
          message="Operação sensível"
          description="Trocar o chip pode bloquear o envio de mensagens até que o novo chip esteja no grupo. Confira antes se o número está adicionado no grupo do time."
          style={{ marginBottom: 16 }}
        />
        <Typography.Paragraph>
          <b>Chip atual:</b> {whatsapp?.instance_name || '—'}<br />
          <b>Grupo do cadastro:</b> {whatsapp?.groupjid || '—'}
        </Typography.Paragraph>
        <Typography.Text strong>Novo chip:</Typography.Text>
        <Select
          value={selectedInstance}
          onChange={setSelectedInstance}
          loading={instancesLoading}
          showSearch
          optionFilterProp="label"
          style={{ width: '100%', marginTop: 8 }}
          placeholder="Selecione a instância"
          options={instances.map((i) => ({
            value: i.name,
            label: `${i.name}${i.phone ? ` — ${i.phone}` : ''}${i.status && i.status !== 'ATIVO' ? ` [${i.status}]` : ''}`,
          }))}
        />
        <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
          A troca vai ser registrada no histórico de atendimento do time.
        </Typography.Paragraph>
      </Modal>

      <Modal
        title={
          viewingMsg
            ? `Mensagem completa · job #${viewingMsg.jobId} · ${viewingMsg.textLen ?? 0} chars`
            : 'Mensagem completa'
        }
        open={!!viewingMsg}
        onCancel={() => setViewingMsg(null)}
        footer={[
          <Button
            key="copy"
            onClick={() => {
              if (viewingMsg?.text) {
                navigator.clipboard.writeText(viewingMsg.text);
                antdMessage.success('Copiado');
              }
            }}
          >
            Copiar texto
          </Button>,
          <Button key="close" type="primary" onClick={() => setViewingMsg(null)}>
            Fechar
          </Button>,
        ]}
        width={720}
      >
        {viewingMsg && (
          <>
            <Space size={8} wrap style={{ marginBottom: 12 }}>
              <Text type="secondary">Enviada em {formatDateTime(viewingMsg.finishedOn || viewingMsg.enqueuedAt)}</Text>
              <DeliveryTag
                delivery={viewingMsg.delivery}
                evolutionStatus={viewingMsg.evolutionStatus}
                failedReason={viewingMsg.failedReason}
              />
            </Space>
            <div
              style={{
                background: '#fafafa',
                border: '1px solid #eee',
                padding: 12,
                borderRadius: 4,
                maxHeight: 400,
                overflowY: 'auto',
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
                fontSize: 13,
              }}
            >
              {viewingMsg.text || viewingMsg.textPreview}
            </div>
            {viewingMsg.failedReason && (
              <Alert
                type="warning"
                showIcon
                message="Motivo da falha"
                description={viewingMsg.failedReason}
                style={{ marginTop: 12 }}
              />
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// Header
// ────────────────────────────────────────────────────────────

const HeaderCard = ({ customer }) => (
  <Card>
    <Row align="middle" gutter={16}>
      <Col>
        <Avatar
          src={absoluteUploadUrl(customer.logo_url) || undefined}
          size={64}
          icon={<TeamOutlined />}
          shape="square"
        />
      </Col>
      <Col flex="auto">
        <Title level={3} style={{ margin: 0 }}>
          {customer.game_name || 'Sem nome'}
        </Title>
        <Space size="middle" wrap style={{ marginTop: 4 }}>
          <StatusTag status={customer.status} />
          {customer.payment_status && (
            <Tag color={customer.payment_status === 'PAGO' ? 'green' : 'orange'}>
              {customer.payment_status}
            </Tag>
          )}
          {customer.trial && <Tag color="purple">TRIAL</Tag>}
          <Text type="secondary">
            {formatDayOfWeek(customer.day_of_game)} • {customer.start_game || '—'} •{' '}
            {customer.players || 0} jogadores + {customer.goalkeepers || 0} goleiros
          </Text>
        </Space>
      </Col>
    </Row>
  </Card>
);

const StatusTag = ({ status }) => {
  if (!status) return <Tag>—</Tag>;
  const color =
    status === 'ATIVO' ? 'green' : status === 'INATIVO' ? 'red' : 'default';
  return <Tag color={color}>{status}</Tag>;
};

// ────────────────────────────────────────────────────────────
// Identidade
// ────────────────────────────────────────────────────────────

const IdentityCard = ({ customer, organizers, onCustomerPatched }) => {
  const owner = organizers?.find((o) => o.role === 'OWNER');
  const extras = organizers?.filter((o) => o !== owner) ?? [];

  const [savingMultiTeam, setSavingMultiTeam] = useState(false);
  const toggleMultiTeam = async (checked) => {
    setSavingMultiTeam(true);
    try {
      const r = await api.patch(`/customers/${customer.id}/multi-team`, { allow_multi_team: checked });
      onCustomerPatched?.({ allow_multi_team: r.data.allow_multi_team });
      antdMessage.success(checked
        ? 'Multi-time liberado — o OWNER pode criar times adicionais.'
        : 'Multi-time desativado.');
    } catch (e) {
      antdMessage.error(e?.response?.data?.message || 'Falhou');
    } finally { setSavingMultiTeam(false); }
  };

  return (
    <Card title={<><TeamOutlined /> Identidade</>}>
      <Descriptions column={1} size="small">
        <Descriptions.Item label="Telefone">
          {customer.cellphone_number || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Multi-time">
          <Space>
            <Switch
              checked={!!customer.allow_multi_team}
              loading={savingMultiTeam}
              onChange={toggleMultiTeam}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {customer.allow_multi_team
                ? 'OWNER pode criar times adicionais (cobrados sem trial)'
                : 'OWNER só administra este time'}
            </Text>
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="Owner">
          {owner ? (
            <>
              {owner.full_name || owner.username}
              {owner.cellphone && (
                <Text type="secondary"> ({owner.cellphone})</Text>
              )}
            </>
          ) : (
            <Text type="secondary">— sem organizador vinculado</Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Outros organizadores">
          {extras.length === 0 ? (
            <Text type="secondary">nenhum</Text>
          ) : (
            <Space direction="vertical" size={0}>
              {extras.map((o) => (
                <Text key={o.id}>
                  {o.full_name || o.username}
                  {o.cellphone && <Text type="secondary"> ({o.cellphone})</Text>}
                </Text>
              ))}
            </Space>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Afiliado">
          {customer.affiliate_code || <Text type="secondary">—</Text>}
        </Descriptions.Item>
        <Descriptions.Item label="Descrição da lista">
          {customer.list_description ? (
            <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
              {customer.list_description}
            </Paragraph>
          ) : (
            <Text type="secondary">—</Text>
          )}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
};

// ────────────────────────────────────────────────────────────
// Assinatura
// ────────────────────────────────────────────────────────────

const SubscriptionCard = ({ subscription, installments }) => {
  if (!subscription) {
    return (
      <Card title={<><CreditCardOutlined /> Assinatura</>}>
        <Empty description="Sem assinatura registrada" />
      </Card>
    );
  }
  return (
    <Card title={<><CreditCardOutlined /> Assinatura</>}>
      <Descriptions column={1} size="small">
        <Descriptions.Item label="Status">
          <Space wrap>
            <Tag color={subscriptionStatusColor(subscription.status)}>
              {subscription.status || '—'}
            </Tag>
            {subscription.type && <Tag color="purple">{subscription.type}</Tag>}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="Plano">
          {subscription.plan_name || `#${subscription.plan_id ?? '—'}`}
          {subscription.plan_price != null && (
            <Text type="secondary"> — R$ {Number(subscription.plan_price).toFixed(2)}</Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Status pagamento">
          <Tag color={paymentStatusColor(subscription.payment_status)}>
            {subscription.payment_status || '—'}
          </Tag>
          {subscription.last_payment_at && (
            <Text type="secondary"> · pago em {formatDate(subscription.last_payment_at)}</Text>
          )}
          {subscription.payment_at && (
            <Text type="secondary"> · vence em {formatDate(subscription.payment_at)}</Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Início">
          {formatDate(subscription.started_at || subscription.created_at)}
        </Descriptions.Item>
        <Descriptions.Item label="Últimas faturas">
          {installments?.length ? (
            <Space direction="vertical" size={0} style={{ width: '100%' }}>
              {installments.map((it, idx) => (
                <Space key={`${it.installment}-${it.created_at}-${idx}`} wrap>
                  <Text type="secondary">#{it.installment ?? idx + 1}</Text>
                  <Text>{formatDate(it.payment_at || it.created_at)}</Text>
                  {it.payment_months && (
                    <Text type="secondary">({it.payment_months}m)</Text>
                  )}
                  <Tag color={paymentStatusColor(it.payment_status)}>
                    {it.payment_status || '—'}
                  </Tag>
                </Space>
              ))}
            </Space>
          ) : (
            <Text type="secondary">—</Text>
          )}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
};

// ────────────────────────────────────────────────────────────
// WhatsApp / Grupo (destaque)
// ────────────────────────────────────────────────────────────

const WhatsAppCard = ({ whatsapp, onEditInstance, onResend, resendingJobId, onViewMsg }) => {
  const evo = whatsapp?.evolution;
  const history = whatsapp?.history;
  const alerts = useMemo(() => buildWhatsappAlerts(whatsapp), [whatsapp]);
  return (
    <Card
      title={
        <>
          <WhatsAppOutlined style={{ color: '#25D366' }} /> WhatsApp / Grupo
        </>
      }
      extra={
        <Button
          icon={<EditOutlined />}
          onClick={onEditInstance}
          size="small"
          danger
        >
          Trocar chip
        </Button>
      }
    >
      <Row gutter={[24, 16]}>
        <Col xs={24} md={12}>
          <Descriptions column={1} size="small" title="Chip (instância)">
            <Descriptions.Item label="Nome">
              {whatsapp?.instance_name || <Text type="secondary">— sem instância</Text>}
            </Descriptions.Item>
            <Descriptions.Item label="Número">
              {whatsapp?.instance_phone || <Text type="secondary">—</Text>}
            </Descriptions.Item>
            <Descriptions.Item label="Status Evolution">
              <StatusEvolutionTag status={whatsapp?.instance_status_evolution} />
            </Descriptions.Item>
          </Descriptions>
        </Col>
        <Col xs={24} md={12}>
          <Descriptions column={1} size="small" title="Grupo">
            <Descriptions.Item label="Sincronizado no cadastro">
              {whatsapp?.groupjid_synced ? (
                <CheckCircleTwoTone twoToneColor="#52c41a" />
              ) : (
                <CloseCircleTwoTone twoToneColor="#ff4d4f" />
              )}{' '}
              <Text type="secondary">{whatsapp?.groupjid || '—'}</Text>
            </Descriptions.Item>
            {evo?.group && (
              <>
                <Descriptions.Item label="Nome do grupo">
                  {evo.group.subject || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Membros">
                  {evo.group.size ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Só admin envia msgs">
                  <BoolTag value={evo.group.announce} truePositive={false} />
                </Descriptions.Item>
                <Descriptions.Item label="Chip está no grupo">
                  <BoolTag value={evo.chipIsMember} truePositive />
                </Descriptions.Item>
                <Descriptions.Item label="Chip é admin">
                  {evo.membershipUnresolvableByLid ? (
                    <Tooltip title="Grupo usa identificadores anônimos (@lid, comunidade). Impossível saber por consulta de API — precisa verificar no WhatsApp.">
                      <Tag color="default">Indeterminado</Tag>
                    </Tooltip>
                  ) : (
                    <BoolTag value={evo.chipIsAdmin} truePositive />
                  )}
                </Descriptions.Item>
              </>
            )}
            {evo && !evo.available && (
              <Descriptions.Item label="Estado">
                <EvolutionUnavailableAlert evo={evo} />
              </Descriptions.Item>
            )}
          </Descriptions>
        </Col>
      </Row>

      {alerts.length > 0 && (
        <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 16 }}>
          {alerts.map((a, i) => (
            <Alert key={i} type={a.type} showIcon message={a.message} description={a.description} />
          ))}
        </Space>
      )}

      <Title level={5} style={{ marginTop: 16 }}>
        Últimas 5 confirmações enviadas ao grupo
      </Title>
      <HistoryList
        history={history}
        onResend={onResend}
        resendingJobId={resendingJobId}
        onViewMsg={onViewMsg}
      />
    </Card>
  );
};

const EvolutionUnavailableAlert = ({ evo }) => {
  if (evo?.reason === 'chip_no_access_to_group') {
    return (
      <Alert
        type="error"
        showIcon
        message="Chip não tem acesso ao grupo"
        description={
          evo.detail ||
          'Evolution respondeu que o chip não pode consultar esse grupo. Provavelmente foi removido ou o groupJid cadastrado está desatualizado.'
        }
      />
    );
  }
  if (evo?.reason === 'evolution_unreachable' || evo?.reason === 'network_error') {
    return (
      <Alert
        type="warning"
        showIcon
        message="Evolution API indisponível"
        description="Não foi possível conectar. Se persistir, verifique a URL/credenciais do backend."
      />
    );
  }
  if (evo?.reason === 'auth_error') {
    return (
      <Alert
        type="error"
        showIcon
        message="Credenciais da Evolution inválidas"
        description="EVOLUTION_API_KEY do backend está errada ou expirou."
      />
    );
  }
  return (
    <Alert
      type="warning"
      showIcon
      message="Dados do grupo indisponíveis"
      description={`reason: ${evo?.reason || 'desconhecido'}${evo?.status ? ` (HTTP ${evo.status})` : ''}`}
    />
  );
};

const HistoryList = ({ history, onResend, resendingJobId, onViewMsg }) => {
  if (!history) {
    return <Empty description="Sem grupo vinculado" />;
  }
  if (history.unavailable) {
    return (
      <Alert
        type="info"
        showIcon
        message="Histórico indisponível"
        description={`Motivo: ${history.reason}`}
      />
    );
  }
  if (!history.items?.length) {
    return (
      <Alert
        type="info"
        showIcon
        message="Sem mensagens recentes desse chip pra esse grupo específico"
        description="Isso é esperado se o time acabou de ser cadastrado, mudou de chip ou não teve confirmação recente. O próximo envio do n8n aparecerá aqui. A fila mantém 3 dias de mensagens entregues e 14 dias de falhas."
      />
    );
  }
  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      {history.items.map((it) => {
        // Só oferece reenviar em falha real na Evolution. accepted já entregou
        // (reenviar duplicaria) e discarded é TTL — msg geralmente foi entregue
        // por outra execução do workflow n8n, então reenviar manualmente também
        // pode duplicar. Se o suporte confirmar que nada chegou, atende via botão
        // de "reenviar" dos accepted/discarded manualmente (não implementado).
        const canResend = it.delivery === 'failed';
        return (
          <Card key={it.jobId} size="small" style={{ background: '#fafafa' }}>
            <Row justify="space-between" align="top" gutter={8}>
              <Col flex="auto">
                <Space size={8} wrap>
                  <Text strong>{formatDateTime(it.finishedOn || it.enqueuedAt)}</Text>
                  <DeliveryTag
                    delivery={it.delivery}
                    evolutionStatus={it.evolutionStatus}
                    failedReason={it.failedReason}
                  />
                  {it.attemptsMade > 1 && (
                    <Tag color="orange">{it.attemptsMade} tentativas</Tag>
                  )}
                </Space>
                <Paragraph
                  type="secondary"
                  style={{ marginTop: 4, marginBottom: 0, whiteSpace: 'pre-wrap' }}
                  ellipsis={{ rows: 2, expandable: true, symbol: 'ver mais' }}
                >
                  {it.textPreview}
                </Paragraph>
                {it.failedReason && (
                  <Text type="danger" style={{ fontSize: 12 }}>
                    {it.failedReason}
                  </Text>
                )}
              </Col>
              <Col>
                <Space direction="vertical" size={4} align="end">
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    job #{it.jobId}
                  </Text>
                  {onViewMsg && (
                    <Tooltip title="Ver mensagem completa">
                      <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => onViewMsg(it)}
                      >
                        Ver completa
                      </Button>
                    </Tooltip>
                  )}
                  {canResend && onResend && (
                    <Tooltip title="Reenviar essa msg usando o chip atual do cadastro">
                      <Button
                        size="small"
                        icon={<SendOutlined />}
                        loading={resendingJobId === it.jobId}
                        onClick={() => onResend(it)}
                      >
                        Reenviar
                      </Button>
                    </Tooltip>
                  )}
                </Space>
              </Col>
            </Row>
          </Card>
        );
      })}
    </Space>
  );
};

// ────────────────────────────────────────────────────────────
// Convocação
// ────────────────────────────────────────────────────────────

const ConvocationCard = ({ convocation }) => (
  <Card title={<><SettingOutlined /> Configuração da Convocação</>}>
    <Descriptions column={1} size="small">
      <Descriptions.Item label="Aviso prévio">
        {formatCallSchedule(convocation.early_call, convocation.custom_time_early_call)}
      </Descriptions.Item>
      <Descriptions.Item label="1ª chamada">
        {formatCallSchedule(convocation.first_call, convocation.custom_time_first_call)}
      </Descriptions.Item>
      <Descriptions.Item label="2ª chamada">
        {formatCallSchedule(convocation.second_call, convocation.custom_time_second_call)}
      </Descriptions.Item>
      <Descriptions.Item label="3ª chamada">
        {formatCallSchedule(convocation.third_call, convocation.custom_time_third_call)}
      </Descriptions.Item>
      <Descriptions.Item label="Manda aviso prévio">
        <BoolTag value={convocation.send_early_notification} truePositive />
      </Descriptions.Item>
      <Descriptions.Item label="Lista de prioridade custom">
        <BoolTag value={convocation.custom_priority_list} truePositive />
      </Descriptions.Item>
    </Descriptions>
  </Card>
);

// ────────────────────────────────────────────────────────────
// Quem recebe cada chamada (first/second/third_call_player_type).
// Aviso prévio sempre vai só pra mensalistas — regra fixa do sistema,
// não é configurável.
// ────────────────────────────────────────────────────────────

const PLAYER_TYPE_LABEL = { 1: 'Só mensalistas', 2: 'Todos (mensalistas + avulsos)' };
const PLAYER_TYPE_COLOR = { 1: 'green', 2: 'blue' };

const CallSettingsCard = ({ customer, canEdit, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState({
    first: customer.first_call_player_type ?? 2,
    second: customer.second_call_player_type ?? 2,
    third: customer.third_call_player_type ?? 2,
  });
  useEffect(() => {
    setValues({
      first: customer.first_call_player_type ?? 2,
      second: customer.second_call_player_type ?? 2,
      third: customer.third_call_player_type ?? 2,
    });
  }, [customer.first_call_player_type, customer.second_call_player_type, customer.third_call_player_type]);

  const dirty = values.first !== (customer.first_call_player_type ?? 2)
             || values.second !== (customer.second_call_player_type ?? 2)
             || values.third !== (customer.third_call_player_type ?? 2);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/customers/${customer.id}/call-config`, {
        first_call_player_type: values.first,
        second_call_player_type: values.second,
        third_call_player_type: values.third,
      });
      onSaved?.({
        first_call_player_type: r.data.first_call_player_type,
        second_call_player_type: r.data.second_call_player_type,
        third_call_player_type: r.data.third_call_player_type,
      });
      antdMessage.success('Configuração de chamadas atualizada (registrado no log)');
    } catch (e) {
      antdMessage.error(e?.response?.data?.message || 'Falhou atualizar');
    } finally { setSaving(false); }
  };

  const renderCell = (field, dbValue) => {
    if (!canEdit) {
      return <Tag color={PLAYER_TYPE_COLOR[dbValue] || 'default'}>{PLAYER_TYPE_LABEL[dbValue] || '—'}</Tag>;
    }
    return (
      <Select
        size="small"
        value={values[field]}
        style={{ minWidth: 220 }}
        onChange={(v) => setValues(s => ({ ...s, [field]: v }))}
        options={[
          { value: 1, label: 'Só Mensalistas' },
          { value: 2, label: 'Todos (Mensalistas + Avulsos)' },
        ]}
      />
    );
  };

  return (
    <Card title={<><TeamOutlined /> Quem recebe cada chamada</>}
      extra={canEdit && dirty && (
        <Button type="primary" size="small" loading={saving} onClick={save}>Salvar alterações</Button>
      )}
    >
      <Descriptions column={1} size="small">
        <Descriptions.Item label="Aviso prévio">
          <Tag color="green">Só mensalistas</Tag>
          <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>(regra fixa)</Text>
        </Descriptions.Item>
        <Descriptions.Item label="1ª chamada">
          {renderCell('first', customer.first_call_player_type)}
        </Descriptions.Item>
        <Descriptions.Item label="2ª chamada">
          {renderCell('second', customer.second_call_player_type)}
        </Descriptions.Item>
        <Descriptions.Item label="3ª chamada">
          {renderCell('third', customer.third_call_player_type)}
        </Descriptions.Item>
      </Descriptions>
      {canEdit && (
        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
          Alterações ficam no log de auditoria (quem mudou, quando, valor antigo/novo).
        </Text>
      )}
    </Card>
  );
};

// ────────────────────────────────────────────────────────────
// Jogos
// ────────────────────────────────────────────────────────────

const GamesCard = ({ games, customerId }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const openDetail = useCallback(async (game) => {
    setDetail({ game, data: null });
    setLoading(true);
    try {
      const r = await api.get(`/customers/${customerId}/games/${game.id}/players`);
      setDetail({ game, data: r.data });
    } catch (err) {
      antdMessage.error('Erro: ' + (err.response?.data?.error || err.message));
      setDetail(null);
    } finally { setLoading(false); }
  }, [customerId]);

  return (
    <Card title={<><CalendarOutlined /> Últimos jogos</>}>
      {(!games || games.length === 0) ? (
        <Empty description="Sem jogos" />
      ) : (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {games.map((g) => (
            <Card key={g.id} size="small" hoverable onClick={() => openDetail(g)} style={{ cursor: 'pointer' }}>
              <Row align="middle" justify="space-between">
                <Col>
                  <Space>
                    <Text strong>{g.date_of_game || formatDate(g.date_of_game_date)}</Text>
                    <Tag color={g.status === 'ABERTO' ? 'blue' : 'default'}>{g.status || '—'}</Tag>
                  </Space>
                  <div style={{ marginTop: 4 }} onClick={(e) => e.stopPropagation()}>
                    <Space size={6} wrap>
                      <CallRecipientsPopover gameId={g.id} callType="EARLY_CALL" sent={g.sent_early_call} endpointBase="/admin/games">
                        <CallDot label="Aviso" sent={g.sent_early_call} scheduledAt={g.date_early_call} clickable />
                      </CallRecipientsPopover>
                      <CallRecipientsPopover gameId={g.id} callType="FIRST_CALL" sent={g.sent_first_call} endpointBase="/admin/games">
                        <CallDot label="1ª" sent={g.sent_first_call} scheduledAt={g.date_first_call} clickable />
                      </CallRecipientsPopover>
                      <CallRecipientsPopover gameId={g.id} callType="SECOND_CALL" sent={g.sent_second_call} endpointBase="/admin/games">
                        <CallDot label="2ª" sent={g.sent_second_call} scheduledAt={g.date_second_call} clickable />
                      </CallRecipientsPopover>
                      <CallRecipientsPopover gameId={g.id} callType="THIRD_CALL" sent={g.sent_third_call} endpointBase="/admin/games">
                        <CallDot label="3ª" sent={g.sent_third_call} scheduledAt={g.date_third_call} clickable />
                      </CallRecipientsPopover>
                    </Space>
                  </div>
                </Col>
                <Col>
                  <Text type="secondary" style={{ fontSize: 11 }}>#{g.id}</Text>
                </Col>
              </Row>
            </Card>
          ))}
        </Space>
      )}
      <Modal
        open={!!detail}
        onCancel={() => setDetail(null)}
        title={detail?.game ? `Jogo #${detail.game.id} — ${detail.game.date_of_game || formatDate(detail.game.date_of_game_date)}` : ''}
        width={800}
        footer={<Button onClick={() => setDetail(null)}>Fechar</Button>}
      >
        {loading && <Spin />}
        {detail?.data && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Row gutter={8}>
              <Col span={6}><Card size="small"><Text type="success" strong>{detail.data.totals.confirmed}</Text> confirmados</Card></Col>
              <Col span={6}><Card size="small"><Text type="danger" strong>{detail.data.totals.declined}</Text> não vão</Card></Col>
              <Col span={6}><Card size="small"><Text type="warning" strong>{detail.data.totals.waitlist}</Text> espera</Card></Col>
              <Col span={6}><Card size="small"><Text type="secondary" strong>{detail.data.totals.no_response}</Text> sem resposta</Card></Col>
            </Row>
            {[
              { key: 'confirmed', label: 'Confirmados', color: 'green' },
              { key: 'declined', label: 'Não vão', color: 'red' },
              { key: 'waitlist', label: 'Lista de espera', color: 'orange' },
              { key: 'no_response', label: 'Sem resposta', color: 'default' },
            ].map(section => (
              detail.data[section.key].length > 0 && (
                <Card key={section.key} size="small" title={<Tag color={section.color}>{section.label} ({detail.data[section.key].length})</Tag>}>
                  <Space wrap size={4}>
                    {detail.data[section.key].map(p => (
                      <Tag key={p.id} style={{ fontSize: 12, padding: '4px 8px' }}>{p.name}</Tag>
                    ))}
                  </Space>
                </Card>
              )
            ))}
          </Space>
        )}
      </Modal>
    </Card>
  );
};

// Card de saúde do time (health score + tags de problemas + chats)
const HealthCard = ({ customerId }) => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/customers/${customerId}/health`);
        setHealth(r.data);
      } catch (err) {
        // Silent - health é bonus
      } finally { setLoading(false); }
    })();
  }, [customerId]);

  if (loading) return <Card><Spin /></Card>;
  if (!health) return null;

  const scoreColor = health.health_score >= 70 ? '#52c41a' : health.health_score >= 40 ? '#faad14' : '#ff4d4f';
  const scoreLabel = health.health_score >= 70 ? 'Saudável' : health.health_score >= 40 ? 'Atenção' : 'Risco';

  return (
    <Card title="🌡️ Saúde do time" size="small">
      <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
        <Col span={12}>
          <Progress type="dashboard" percent={health.health_score} strokeColor={scoreColor} format={v => `${v}%`} width={120} />
          <div style={{ textAlign: 'center' }}><Tag color={scoreColor}>{scoreLabel}</Tag></div>
        </Col>
        <Col span={12}>
          <Space direction="vertical" size={4}>
            <div>{health.signals.status_ativo ? '✅' : '❌'} Status ATIVO</div>
            <div>{health.signals.subscription_ok ? '✅' : '❌'} Assinatura em dia</div>
            <div>{health.signals.recent_games_ok ? '✅' : '❌'} Convocações recentes ok</div>
            <div>{health.signals.wa_reply_recent ? '✅' : '❌'} Respondeu WhatsApp {'<'}30d</div>
            <div>{health.signals.group_synced ? '✅' : '❌'} Grupo sincronizado</div>
            {health.signals.days_since_last_signal !== null && (
              <div>{health.signals.days_since_last_signal > 60 ? '✅' : '⚠️'} Última queixa: há {health.signals.days_since_last_signal}d</div>
            )}
          </Space>
        </Col>
      </Row>
      {health.support_tags.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Tipos de problema já relatados:</Text>
          <div style={{ marginTop: 4 }}>
            <Space wrap size={4}>
              {health.support_tags.map(t => <Tag key={t} color="volcano">{t}</Tag>)}
            </Space>
          </div>
        </div>
      )}
      <div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Conversas Chatwoot: {health.chats.total} total, <Text strong style={{ color: '#ff4d4f' }}>{health.chats.signal_count}</Text> com ruído
        </Text>
        {health.chats.list.filter(c => c.is_support_signal).length > 0 && (
          <div style={{ marginTop: 8, maxHeight: 200, overflowY: 'auto' }}>
            {health.chats.list.filter(c => c.is_support_signal).slice(0, 10).map(c => (
              <div key={c.conversation_id} style={{ borderLeft: '3px solid #ff4d4f', padding: '4px 8px', marginBottom: 4, background: '#fff2f0', cursor: 'pointer' }} onClick={() => setChatOpen(c)}>
                <Text style={{ fontSize: 11 }}>
                  #{c.conversation_id} • {c.message_count} msgs
                  {c.tags.length > 0 && <> • <Text type="secondary">{c.tags.join(', ')}</Text></>}
                </Text>
              </div>
            ))}
          </div>
        )}
      </div>
      <Modal
        open={!!chatOpen} onCancel={() => setChatOpen(null)}
        title={chatOpen ? `Conversa #${chatOpen.conversation_id}` : ''}
        width={700}
        footer={<>
          {chatOpen && <a href={chatOpen.chatwoot_link} target="_blank" rel="noreferrer"><Button>Abrir no Chatwoot ↗</Button></a>}
          <Button onClick={() => setChatOpen(null)} style={{ marginLeft: 8 }}>Fechar</Button>
        </>}
      >
        {chatOpen && (
          <div>
            <Space wrap style={{ marginBottom: 8 }}>
              {chatOpen.tags.map(t => <Tag key={t} color="volcano">{t}</Tag>)}
            </Space>
            <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, whiteSpace: 'pre-wrap', fontSize: 12, maxHeight: 400, overflowY: 'auto' }}>{chatOpen.preview}</div>
          </div>
        )}
      </Modal>
    </Card>
  );
};

// ────────────────────────────────────────────────────────────
// Jogadores
// ────────────────────────────────────────────────────────────

const PlayersCard = ({ players }) => {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('todos');

  const stats = useMemo(() => {
    if (!players?.length) return { total: 0, ativos: 0, goleiros: 0, isentos: 0 };
    return {
      total: players.length,
      ativos: players.filter((p) => (p.status || '').toUpperCase() === 'ATIVO').length,
      goleiros: players.filter((p) => (p.type || '').toUpperCase() === 'GOLEIRO').length,
      isentos: players.filter((p) => p.is_charge_exempt || p.charge_free).length,
    };
  }, [players]);

  const rows = useMemo(() => {
    let items = players || [];
    if (typeFilter === 'goleiro') {
      items = items.filter((p) => (p.type || '').toUpperCase() === 'GOLEIRO');
    } else if (typeFilter === 'mensalista') {
      items = items.filter((p) => (p.type || '').toUpperCase() !== 'GOLEIRO');
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      items = items.filter(
        (p) =>
          (p.name || '').toLowerCase().includes(q) ||
          (p.phone_number || '').toLowerCase().includes(q),
      );
    }
    return items;
  }, [players, search, typeFilter]);

  const columns = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      render: (name, row) => (
        <div>
          <Text strong>{name || '—'}</Text>
          {row.position && (
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>{row.position}</Text>
            </div>
          )}
        </div>
      ),
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Telefone',
      dataIndex: 'phone_number',
      key: 'phone',
      width: 150,
      render: (v) => v ? <Text code>{v}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
      width: 110,
      render: (t) => {
        const up = (t || '').toUpperCase();
        if (up === 'GOLEIRO') return <Tag color="orange">Goleiro</Tag>;
        if (up) return <Tag color="blue">{up}</Tag>;
        return <Tag>—</Tag>;
      },
      filters: [
        { text: 'Mensalista', value: 'MENSALISTA' },
        { text: 'Goleiro', value: 'GOLEIRO' },
      ],
      onFilter: (val, row) => (row.type || '').toUpperCase() === val,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s) => {
        const up = (s || '').toUpperCase();
        if (up === 'ATIVO') return <Badge status="success" text="Ativo" />;
        if (up === 'INATIVO') return <Badge status="default" text="Inativo" />;
        if (up === 'AFASTADO') return <Badge status="warning" text="Afastado" />;
        return <Text type="secondary">{s || '—'}</Text>;
      },
    },
    {
      title: 'Nível',
      dataIndex: 'technical_level',
      key: 'level',
      width: 80,
      align: 'center',
      render: (v) => v != null ? <Tag>{v}</Tag> : <Text type="secondary">—</Text>,
      sorter: (a, b) => (a.technical_level || 0) - (b.technical_level || 0),
    },
    {
      title: 'Cobrança',
      key: 'charge',
      width: 120,
      render: (_, row) => {
        if (row.charge_free) return <Tag color="purple">Grátis</Tag>;
        if (row.is_charge_exempt) return <Tag color="cyan">Isento</Tag>;
        return <Tag color="default">Cobrado</Tag>;
      },
    },
    {
      title: 'Cadastro',
      dataIndex: 'created_at',
      key: 'created',
      width: 110,
      render: (v) => v ? formatDate(v) : <Text type="secondary">—</Text>,
      sorter: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
    },
  ];

  return (
    <Card
      title={
        <Space>
          <UsergroupAddOutlined />
          <span>Jogadores cadastrados</span>
          <Badge count={stats.total} showZero style={{ backgroundColor: '#1677ff' }} />
        </Space>
      }
      extra={
        <Space size={12}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {stats.ativos} ativos · {stats.goleiros} goleiros · {stats.isentos} isentos
          </Text>
        </Space>
      }
    >
      {(!players || players.length === 0) ? (
        <Empty description="Sem jogadores cadastrados" />
      ) : (
        <>
          <Space style={{ marginBottom: 12, width: '100%' }} wrap>
            <Input.Search
              placeholder="Buscar por nome ou telefone"
              allowClear
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 260 }}
            />
            <Select
              value={typeFilter}
              onChange={setTypeFilter}
              options={[
                { value: 'todos', label: 'Todos' },
                { value: 'mensalista', label: 'Mensalistas' },
                { value: 'goleiro', label: 'Goleiros' },
              ]}
              style={{ width: 140 }}
            />
          </Space>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={rows}
            size="small"
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `${t} jogadores` }}
          />
        </>
      )}
    </Card>
  );
};

// ────────────────────────────────────────────────────────────
// Chips membros do grupo (com validação + seleção do chip principal)
// ────────────────────────────────────────────────────────────

const GroupMembersCard = ({ customerId, onInstanceChanged }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [settingChip, setSettingChip] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchGroupMembers(customerId);
      setMembers(r.members || []);
    } catch (e) {
      antdMessage.error('Erro ao carregar chips do grupo');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const handleValidate = async () => {
    setValidating(true);
    try {
      const r = await validateGroupMembers(customerId);
      antdMessage.success(`Validação concluída — ${r.in_group_count} chip(s) no grupo`);
      await load();
    } catch (e) {
      antdMessage.error(e.response?.data?.message || 'Erro ao validar');
    } finally {
      setValidating(false);
    }
  };

  const doSet = async (instance, force) => {
    setSettingChip(instance);
    try {
      const r = await setCustomerInstance(customerId, instance, force);
      antdMessage.success(`Chip alterado para ${r.instance_name}`);
      await load();
      if (onInstanceChanged) onInstanceChanged();
    } catch (e) {
      const data = e.response?.data;
      if (data?.warning === 'not_admin_in_announce_group') {
        Modal.confirm({
          title: 'Chip não é admin em grupo announce=true',
          content: `Neste grupo, só admins podem enviar mensagens e ${instance} NÃO é admin — as msgs vão falhar. Confirma mesmo assim?`,
          okText: 'Sim, forçar',
          okType: 'danger',
          cancelText: 'Cancelar',
          onOk: () => doSet(instance, true),
        });
      } else {
        antdMessage.error(data?.message || 'Erro ao definir chip');
      }
    } finally {
      setSettingChip(null);
    }
  };

  const handleSet = (instance) => doSet(instance, false);

  const columns = [
    {
      title: 'Chip',
      dataIndex: 'instance_name',
      key: 'name',
      render: (name, row) => (
        <Space direction="vertical" size={0}>
          <Space size={6}>
            <Text strong>{name}</Text>
            {row.is_current_sender && <Tag color="green">Envio atual</Tag>}
          </Space>
          {row.phone && (
            <Text type="secondary" style={{ fontSize: 11 }}>{row.phone}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Papel no grupo',
      dataIndex: 'is_admin',
      key: 'role',
      width: 140,
      align: 'center',
      render: (v) => {
        if (v === true) return <Tag color="gold" icon={<CrownOutlined />}>Admin</Tag>;
        if (v === false) return <Tag>Membro</Tag>;
        return <Tag color="default">?</Tag>;
      },
    },
    {
      title: 'Status chip',
      dataIndex: 'instance_status',
      key: 'status',
      width: 130,
      align: 'center',
      render: (s) => {
        if (s === 'ATIVO') return <Badge status="success" text="Ativo" />;
        if (s === 'LIMITE_ATINGIDO') return <Badge status="warning" text="Limite" />;
        if (s === 'INATIVO') return <Badge status="default" text="Inativo" />;
        return <Text type="secondary">{s || '—'}</Text>;
      },
    },
    {
      title: 'Última validação',
      dataIndex: 'last_validated_at',
      key: 'validated',
      width: 160,
      render: (v) => v ? formatDateTime(v) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 180,
      align: 'right',
      render: (_, row) => (
        row.is_current_sender ? (
          <Text type="secondary" style={{ fontSize: 12 }}>chip atual</Text>
        ) : (
          <Button
            type="primary"
            size="small"
            loading={settingChip === row.instance_name}
            onClick={() => handleSet(row.instance_name)}
          >
            Definir para envio
          </Button>
        )
      ),
    },
  ];

  return (
    <Card
      title={
        <Space>
          <WhatsAppOutlined style={{ color: '#25D366' }} />
          <span>Chips no grupo do WhatsApp</span>
          <Badge count={members.filter((m) => m.is_current_sender !== null).length} showZero style={{ backgroundColor: '#1677ff' }} />
        </Space>
      }
      extra={
        <Button
          icon={<ReloadOutlined />}
          loading={validating}
          onClick={handleValidate}
        >
          Validar no grupo
        </Button>
      }
    >
      {loading ? (
        <div style={{ padding: 20, textAlign: 'center' }}><Spin /></div>
      ) : members.length === 0 ? (
        <Empty description={
          <div>
            <div>Ainda não validamos os chips desse grupo.</div>
            <div style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Clique em "Validar no grupo" para descobrir quais chips estão no grupo.
              </Text>
            </div>
          </div>
        } />
      ) : (
        <Table
          rowKey="instance_name"
          columns={columns}
          dataSource={members}
          size="small"
          pagination={false}
        />
      )}
    </Card>
  );
};

// ────────────────────────────────────────────────────────────
// Suporte
// ────────────────────────────────────────────────────────────

const SupportLogsCard = ({ supportLogs }) => (
  <Card title="Histórico de atendimento">
    {(!supportLogs || supportLogs.length === 0) ? (
      <Empty description="Sem entradas de suporte" />
    ) : (
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        {supportLogs.map((log) => (
          <Row key={log.id} gutter={16}>
            <Col style={{ minWidth: 140 }}>
              <Text type="secondary">{formatDateTime(log.created_at)}</Text>
            </Col>
            <Col style={{ minWidth: 120 }}>
              <Text strong>{log.author_name || 'sistema'}</Text>
            </Col>
            <Col flex="auto">
              <Text>{log.description}</Text>
            </Col>
          </Row>
        ))}
      </Space>
    )}
  </Card>
);

const WelcomeAudioFlowCard = ({ flow, customer }) => {
  if (!flow) {
    return (
      <Card title="Fluxo de áudios de boas-vindas" size="small">
        <Empty description="Este time não passou pelo fluxo de áudios welcome" />
      </Card>
    );
  }
  const stateMap = {
    waiting_reply: { color: 'orange', label: 'Aguardando resposta (janela fechada)' },
    sending_audios: { color: 'blue', label: 'Enviando áudios (janela aberta)' },
    waiting_reengage: { color: 'gold', label: 'Aguardando 6h pra retry' },
    completed: { color: 'green', label: 'Concluído (todos áudios enviados)' },
    abandoned: { color: 'red', label: 'Abandonado (3 rodadas sem resposta)' },
  };
  const s = stateMap[flow.state] || { color: 'default', label: flow.state };
  const openedWindow = !!flow.window_opened_at;
  return (
    <Card title="Fluxo de áudios de boas-vindas" size="small">
      <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
        <Descriptions.Item label="Status"><Tag color={s.color}>{s.label}</Tag></Descriptions.Item>
        <Descriptions.Item label="Progresso">{flow.current_index}/{flow.total_audios} áudios enviados</Descriptions.Item>
        <Descriptions.Item label="Interagiu?">
          {openedWindow
            ? <Tag color="green" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}>Sim</Tag>
            : <Tag color="orange">Ainda não</Tag>}
        </Descriptions.Item>
        <Descriptions.Item label="Tentativas rodada atual">{flow.attempts_current}/3</Descriptions.Item>
        <Descriptions.Item label="Rodada de reengajamento">{flow.reengage_round}/3</Descriptions.Item>
        <Descriptions.Item label="Último envio">{flow.last_send_at ? formatDateTime(flow.last_send_at) + ' (' + (flow.last_mode || '—') + ')' : '—'}</Descriptions.Item>
        {flow.window_opened_at && (
          <Descriptions.Item label="Janela aberta em">{formatDateTime(flow.window_opened_at)}</Descriptions.Item>
        )}
        {flow.completed_at && (
          <Descriptions.Item label="Concluído em">{formatDateTime(flow.completed_at)}</Descriptions.Item>
        )}
        <Descriptions.Item label="Iniciado em">{formatDateTime(flow.started_at)}</Descriptions.Item>
        {flow.error_message && (
          <Descriptions.Item label="Erro" span={3}><Text type="danger">{flow.error_message}</Text></Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  );
};

// ────────────────────────────────────────────────────────────
// Componentes reutilizáveis
// ────────────────────────────────────────────────────────────

const BoolTag = ({ value, truePositive }) => {
  if (value === true) {
    return truePositive ? (
      <Tag color="green" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}>Sim</Tag>
    ) : (
      <Tag color="red" icon={<ExclamationCircleTwoTone twoToneColor="#ff4d4f" />}>Sim</Tag>
    );
  }
  if (value === false) {
    return truePositive ? (
      <Tag color="red" icon={<CloseCircleTwoTone twoToneColor="#ff4d4f" />}>Não</Tag>
    ) : (
      <Tag color="green" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}>Não</Tag>
    );
  }
  return <Tag color="default">—</Tag>;
};

const StatusEvolutionTag = ({ status }) => {
  if (!status) return <Tag>—</Tag>;
  const color = status === 'open' ? 'green' : status === 'close' ? 'red' : 'orange';
  return <Tag color={color}>{status.toUpperCase()}</Tag>;
};

const DeliveryTag = ({ delivery, evolutionStatus, failedReason }) => {
  const spec = {
    accepted: {
      color: 'green',
      label: 'Aceita pelo WhatsApp',
      tooltip:
        'Evolution recebeu com sucesso e passou pro WhatsApp Web. A confirmação de entrega no grupo é assíncrona (via webhook que a fila não escuta), então não vemos "entregue" aqui — o caminho normal é este.',
    },
    discarded: {
      color: 'default',
      label: 'Descartada pela fila (TTL)',
      tooltip:
        'Este job específico ficou 15+ min esperando (rate limit ou fila cheia) e foi descartado. Isso NÃO significa que o time deixou de receber a mensagem — o workflow do n8n costuma reenviar a mesma lista em várias chamadas (5d, 3d, 2d, 1d antes do jogo), então outra execução provavelmente entregou.',
    },
    failed: {
      color: 'red',
      label: 'Falha na Evolution',
      tooltip: failedReason ? `Motivo: ${failedReason}` : undefined,
    },
    // legado (versões antigas do queue-service)
    sent: { color: 'green', label: `Enviada (${evolutionStatus || 'sent'})` },
    pending: { color: 'blue', label: 'Aguardando WhatsApp' },
  }[delivery] || { color: 'default', label: delivery || '—' };
  const tag = <Tag color={spec.color}>{spec.label}</Tag>;
  return spec.tooltip ? <Tooltip title={spec.tooltip}>{tag}</Tooltip> : tag;
};

const CallDot = ({ label, sent, scheduledAt, clickable }) => {
  const parts = [`${label} — ${sent ? 'enviada' : 'não enviada'}`];
  if (scheduledAt && scheduledAt !== 'null') parts.push(`programada: ${scheduledAt}`);
  else if (!scheduledAt) parts.push('sem data programada');
  if (clickable && sent) parts.push('clique pra ver destinatarios');
  return (
    <Tooltip title={parts.join(' · ')}>
      <Tag color={sent ? 'green' : 'default'} style={clickable && sent ? { cursor: 'pointer' } : undefined}>
        {label}
      </Tag>
    </Tooltip>
  );
};

// ────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────

const dayNames = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];
function formatDayOfWeek(dow) {
  if (dow == null || dow === '') return '—';
  const n = Number(dow);
  if (Number.isFinite(n) && n >= 0 && n <= 6) return dayNames[n];
  return String(dow);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('pt-BR');
  } catch {
    return dateStr;
  }
}

function formatDateTime(input) {
  if (!input) return '—';
  try {
    const d = typeof input === 'number' ? new Date(input) : new Date(input);
    if (Number.isNaN(d.getTime())) return String(input);
    return d.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(input);
  }
}

function formatCallSchedule(daysBefore, customTime) {
  if (daysBefore == null) return '—';
  const days = Number(daysBefore);
  const dayLabel = days === 0 ? 'no dia' : `${days} dia${days > 1 ? 's' : ''} antes`;
  if (customTime) return `${dayLabel} às ${customTime}`;
  return dayLabel;
}

function subscriptionStatusColor(status) {
  if (status === 'ATIVO' || status === 'PLANO_ATIVO') return 'green';
  if (status === 'PLANO_EXPIRADO' || status === 'INATIVO') return 'red';
  if (status === 'TRIAL') return 'purple';
  if (status?.includes('PENDENTE')) return 'orange';
  return 'default';
}

function paymentStatusColor(status) {
  if (!status) return 'default';
  const s = String(status).toUpperCase();
  if (s === 'PAGO' || s === 'PAID' || s === 'APROVADO') return 'green';
  if (s === 'PENDENTE' || s === 'PENDING' || s === 'AGUARDANDO') return 'orange';
  if (s === 'ATRASADO' || s === 'VENCIDO' || s === 'FAILED' || s === 'REJECTED') return 'red';
  return 'default';
}

function buildWhatsappAlerts(whatsapp) {
  const alerts = [];
  if (!whatsapp?.instance_name) {
    alerts.push({
      type: 'warning',
      message: 'Time sem instância WhatsApp',
      description: 'Nenhum chip está vinculado ao time. Não haverá envio de convocações.',
    });
    return alerts;
  }
  if (!whatsapp?.groupjid) {
    alerts.push({
      type: 'warning',
      message: 'Grupo não sincronizado',
      description: 'O organizador precisa sincronizar o grupo pra permitir envio das listas.',
    });
    return alerts;
  }
  const evo = whatsapp.evolution;

  // Único sinal 100% confiável de "chip fora do grupo": Evolution devolveu
  // 404/forbidden. Nesse caso, msgs realmente não chegam. Correlação por número
  // dá falso negativo em grupos de comunidade (todos os IDs são @lid), então
  // NÃO alertamos com base em chipIsMember=false sozinho — só com o reason da API.
  if (evo && !evo.available && evo.reason === 'chip_no_access_to_group') {
    alerts.push({
      type: 'error',
      message: 'Chip fora do grupo',
      description:
        'Evolution retornou 404/forbidden ao consultar esse grupo — o chip não é membro. As msgs vão ser aceitas pela API mas nunca vão chegar ao grupo. Alguém precisa adicionar o chip ao grupo, ou o groupJid do cadastro precisa ser atualizado.',
    });
  }

  // "Só admin envia" + chip explicitamente não-admin: alerta amarelo. Se o admin
  // for indeterminado (@lid), não alerta — pode ser admin sem a gente saber.
  if (evo?.available && evo.group?.announce === true && evo.chipIsAdmin === false && !evo.membershipUnresolvableByLid) {
    alerts.push({
      type: 'warning',
      message: 'Grupo "só admin envia" e o chip não é admin',
      description:
        'Nesse tipo de grupo só admins podem postar. Um admin precisa promover o chip, ou você troca por outro chip que já seja admin desse grupo.',
    });
  }
  return alerts;
}

export default CustomerFicha;
